import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { assetUrl } from './assetUrl';
import { AABB, Climbable, Interactable, PlayBounds, makeAABB } from './types';
import {
  KeepMaterials,
  createKeepMaterials,
  setBoxWorldUVs,
  setPlaneWorldUVs,
} from './materials';
import { boundsFromRooms, buildKeepWing, RoomInfo } from './Rooms';

export type WorldData = {
  solids: AABB[];
  climbables: Climbable[];
  interactables: Interactable[];
  spawn: THREE.Vector3;
  companionSpawn: THREE.Vector3;
  gateMesh: THREE.Object3D;
  /** Full lever assembly (pedestal + arm). */
  leverPivot: THREE.Group;
  /** Rotating arm only — animate `rotation.z` from ~0.72 → ~-0.95 when pulled. */
  leverArm: THREE.Group;
  playBounds: PlayBounds;
  rooms: RoomInfo[];
};

/** Soft clamp — recomputed around the loaded fort. */
export let PLAY_BOUNDS: PlayBounds = {
  minX: -18,
  maxX: 18,
  minZ: -22,
  maxZ: 22,
  killY: -4,
};

const FORT_URL = assetUrl('levels/modular_fort_01/modular_fort_01.gltf');

function addBox(
  parent: THREE.Object3D,
  mat: THREE.Material,
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
  solids: AABB[],
  solid = true,
  metersPerTile = 2.2,
): THREE.Mesh {
  const geo = new THREE.BoxGeometry(sx, sy, sz);
  setBoxWorldUVs(geo, sx, sy, sz, metersPerTile);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(cx, cy, cz);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  if (solid) solids.push(makeAABB(cx, cy, cz, sx / 2, sy / 2, sz / 2));
  return mesh;
}

function addPerimeterShell(solids: AABB[], bounds: PlayBounds): void {
  const pad = 2.5;
  const t = 3.5;
  const h = 24;
  const cy = 10;
  const x0 = bounds.minX - pad;
  const x1 = bounds.maxX + pad;
  const z0 = bounds.minZ - pad;
  const z1 = bounds.maxZ + pad;
  const spanX = x1 - x0;
  const spanZ = z1 - z0;
  solids.push(makeAABB(0, cy, z0 - t * 0.5, spanX * 0.5 + t, h * 0.5, t * 0.5));
  solids.push(makeAABB(0, cy, z1 + t * 0.5, spanX * 0.5 + t, h * 0.5, t * 0.5));
  solids.push(makeAABB(x0 - t * 0.5, cy, 0, t * 0.5, h * 0.5, spanZ * 0.5 + t));
  solids.push(makeAABB(x1 + t * 0.5, cy, 0, t * 0.5, h * 0.5, spanZ * 0.5 + t));
}

function addLadder(
  parent: THREE.Object3D,
  mats: KeepMaterials,
  climbables: Climbable[],
  opts: {
    centerX: number;
    centerZ: number;
    bottomY: number;
    topY: number;
    normal: { x: number; y: number; z: number };
    width?: number;
  },
): void {
  const width = opts.width ?? 0.9;
  const height = opts.topY - opts.bottomY;
  const midY = (opts.bottomY + opts.topY) * 0.5;
  const n = opts.normal;
  const nx = n.x;
  const nz = n.z;
  const tx = -nz;
  const tz = nx;

  const group = new THREE.Group();
  group.name = 'ladder';
  group.position.set(opts.centerX, 0, opts.centerZ);
  group.rotation.y = Math.atan2(nx, nz);
  parent.add(group);

  const halfW = width * 0.5;
  const railH = height + 0.08;
  for (const side of [-1, 1]) {
    const rail = new THREE.Mesh(new THREE.BoxGeometry(0.07, railH, 0.08), mats.wood);
    setBoxWorldUVs(rail.geometry as THREE.BoxGeometry, 0.07, railH, 0.08, 0.7);
    rail.position.set(side * halfW, midY, 0);
    rail.castShadow = true;
    group.add(rail);
  }
  const rungCount = Math.max(4, Math.round(height / 0.42));
  for (let i = 0; i < rungCount; i++) {
    const t = (i + 0.55) / rungCount;
    const y = opts.bottomY + t * height;
    const rung = new THREE.Mesh(
      new THREE.BoxGeometry(width + 0.04, 0.055, 0.07),
      mats.wood,
    );
    rung.position.set(0, y, 0.02);
    rung.castShadow = true;
    group.add(rung);
  }

  // Soft emissive affordance so ladders read before the prompt
  const glowMat = new THREE.MeshBasicMaterial({
    color: 0xffc98a,
    transparent: true,
    opacity: 0.22,
    depthWrite: false,
  });
  const glow = new THREE.Mesh(new THREE.BoxGeometry(width + 0.2, height * 0.92, 0.04), glowMat);
  glow.position.set(0, midY, 0.06);
  glow.renderOrder = 3;
  group.add(glow);

  const approachDepth = 1.15;
  const ax = opts.centerX + nx * (approachDepth * 0.35);
  const az = opts.centerZ + nz * (approachDepth * 0.35);
  climbables.push({
    aabb: makeAABB(
      ax,
      midY,
      az,
      Math.abs(tx) * halfW + Math.abs(nx) * approachDepth * 0.55 + 0.35,
      height * 0.5 + 0.35,
      Math.abs(tz) * halfW + Math.abs(nz) * approachDepth * 0.55 + 0.35,
    ),
    normal: { x: nx, y: 0, z: nz },
    bottomY: opts.bottomY,
    topY: opts.topY,
    centerX: opts.centerX,
    centerZ: opts.centerZ,
  });
}

function aabbFromBox3(box: THREE.Box3): AABB {
  return {
    minX: box.min.x,
    maxX: box.max.x,
    minY: box.min.y,
    maxY: box.max.y,
    minZ: box.min.z,
    maxZ: box.max.z,
  };
}

/** Dirt / damp / moss tint on shipped fort materials. */
function weatherFortMaterials(fort: THREE.Object3D): void {
  fort.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (let i = 0; i < list.length; i++) {
      const src = list[i] as THREE.MeshStandardMaterial;
      if (!src?.isMeshStandardMaterial) continue;
      const m = src.clone();
      m.color.offsetHSL(0.04, 0.06, -0.08);
      m.color.multiplyScalar(0.9);
      m.roughness = Math.min(1, (m.roughness ?? 0.7) + 0.18);
      m.metalness = Math.max(0, (m.metalness ?? 0) * 0.7);
      m.envMapIntensity = 0.55;
      if (Array.isArray(mesh.material)) mesh.material[i] = m;
      else mesh.material = m;
    }
  });
}

/** Low moss carpets + wall seepage patches for age. */
function scatterMoss(
  parent: THREE.Object3D,
  mats: KeepMaterials,
  box: THREE.Box3,
  center: THREE.Vector3,
): void {
  const mossMat = mats.moss;
  const rng = (n: number) => {
    const x = Math.sin(n * 127.1) * 43758.5453;
    return x - Math.floor(x);
  };

  // Courtyard floor carpets
  for (let i = 0; i < 28; i++) {
    const a = rng(i * 3.1) * Math.PI * 2;
    const r = 2.5 + rng(i * 7.7) * Math.min(box.max.x - box.min.x, box.max.z - box.min.z) * 0.28;
    const sx = 0.55 + rng(i * 2.2) * 1.4;
    const sz = 0.4 + rng(i * 5.5) * 1.1;
    const geo = new THREE.PlaneGeometry(sx, sz);
    setPlaneWorldUVs(geo, sx, sz, 1.1);
    const patch = new THREE.Mesh(geo, mossMat);
    patch.rotation.x = -Math.PI / 2;
    patch.rotation.z = rng(i * 9.1) * Math.PI;
    patch.position.set(
      center.x + Math.cos(a) * r,
      0.025 + rng(i) * 0.02,
      center.z + Math.sin(a) * r * 0.85,
    );
    patch.receiveShadow = true;
    patch.renderOrder = 1;
    parent.add(patch);
  }

  // Vertical seepage on lower walls (north/south-ish edges)
  for (let i = 0; i < 18; i++) {
    const alongWall = rng(i * 11.3);
    const side = i % 2 === 0 ? 1 : -1;
    const w = 0.7 + rng(i * 4.4) * 1.6;
    const h = 0.9 + rng(i * 6.6) * 1.8;
    const geo = new THREE.PlaneGeometry(w, h);
    setPlaneWorldUVs(geo, w, h, 1.0);
    const stain = new THREE.Mesh(geo, mossMat);
    const z = side > 0 ? box.max.z - 0.35 : box.min.z + 0.35;
    stain.position.set(
      box.min.x + alongWall * (box.max.x - box.min.x),
      0.35 + h * 0.35,
      z,
    );
    stain.rotation.y = side > 0 ? Math.PI : 0;
    stain.receiveShadow = true;
    parent.add(stain);
  }

  // Corner clumps
  const corners: [number, number][] = [
    [box.min.x + 1.2, box.min.z + 1.2],
    [box.max.x - 1.2, box.min.z + 1.2],
    [box.min.x + 1.2, box.max.z - 1.2],
    [box.max.x - 1.2, box.max.z - 1.2],
  ];
  for (let c = 0; c < corners.length; c++) {
    const [cx, cz] = corners[c];
    for (let j = 0; j < 3; j++) {
      const s = 0.45 + rng(c * 10 + j) * 0.55;
      const geo = new THREE.BoxGeometry(s, 0.12 + rng(j + c) * 0.1, s * 0.7);
      setBoxWorldUVs(geo, s, 0.15, s * 0.7, 0.8);
      const clump = new THREE.Mesh(geo, mossMat);
      clump.position.set(cx + (rng(j) - 0.5) * 0.8, 0.08, cz + (rng(j + 3) - 0.5) * 0.8);
      clump.castShadow = true;
      clump.receiveShadow = true;
      parent.add(clump);
    }
  }
}

/**
 * Weathered gate winch — stone plinth, iron housing, hinged timber arm.
 * Returns [assemblyRoot, rotatingArm].
 */
function addGateLever(
  parent: THREE.Object3D,
  mats: KeepMaterials,
  x: number,
  z: number,
  solids: AABB[],
): { root: THREE.Group; arm: THREE.Group } {
  const root = new THREE.Group();
  root.name = 'GateLever';
  root.position.set(x, 0, z);

  // Face roughly toward courtyard center
  root.rotation.y = Math.atan2(-x, -z) + Math.PI;

  const stone = mats.wallDark;
  const iron = mats.metal;
  const wood = mats.wood;
  const moss = mats.moss;
  const brass = mats.metalWarm;

  // Tiered stone pedestal
  const plinthLow = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.35, 0.95), stone);
  setBoxWorldUVs(plinthLow.geometry as THREE.BoxGeometry, 1.15, 0.35, 0.95, 1.4);
  plinthLow.position.y = 0.175;
  const plinthHi = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.55, 0.7), stone);
  setBoxWorldUVs(plinthHi.geometry as THREE.BoxGeometry, 0.85, 0.55, 0.7, 1.4);
  plinthHi.position.y = 0.62;
  root.add(plinthLow, plinthHi);

  // Moss on plinth edges
  const mossRim = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.08, 0.35), moss);
  mossRim.position.set(0, 0.32, 0.32);
  const mossSide = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.1, 0.9), moss);
  mossSide.position.set(0.48, 0.2, 0);
  root.add(mossRim, mossSide);

  // Iron housing / cheek plates
  const cheekGeo = new THREE.BoxGeometry(0.1, 0.55, 0.42);
  const cheekL = new THREE.Mesh(cheekGeo, iron);
  const cheekR = new THREE.Mesh(cheekGeo, iron);
  cheekL.position.set(-0.28, 1.15, 0);
  cheekR.position.set(0.28, 1.15, 0);
  const crossbar = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.1, 0.12), iron);
  crossbar.position.set(0, 0.95, -0.12);
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.16, 0.12, 12), iron);
  cap.rotation.z = Math.PI / 2;
  cap.position.set(0, 1.15, 0);
  root.add(cheekL, cheekR, crossbar, cap);

  // Pivot pin
  const pin = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.62, 10), brass);
  pin.rotation.z = Math.PI / 2;
  pin.position.set(0, 1.15, 0);
  root.add(pin);

  // Rotating arm (hinge at pin)
  const arm = new THREE.Group();
  arm.name = 'LeverArm';
  arm.position.set(0, 1.15, 0);
  arm.rotation.z = 0.72; // raised / ready

  const shaft = new THREE.Mesh(new THREE.BoxGeometry(0.09, 1.05, 0.09), wood);
  setBoxWorldUVs(shaft.geometry as THREE.BoxGeometry, 0.09, 1.05, 0.09, 0.6);
  shaft.position.set(0.42, 0, 0);
  shaft.rotation.z = -Math.PI / 2;

  const wrap = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.35, 8), iron);
  wrap.rotation.z = Math.PI / 2;
  wrap.position.set(0.22, 0, 0);

  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 0.28, 10), wood);
  grip.rotation.z = Math.PI / 2;
  grip.position.set(0.88, 0, 0);

  const knob = new THREE.Mesh(new THREE.SphereGeometry(0.11, 14, 12), brass);
  knob.position.set(1.02, 0, 0);
  // Soft “pull me” warmth while unused
  const knobMat = (knob.material as THREE.MeshStandardMaterial).clone();
  knobMat.emissive = new THREE.Color(0x5a3010);
  knobMat.emissiveIntensity = 0.35;
  knob.material = knobMat;
  knob.name = 'LeverKnob';

  const counter = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 0.16), iron);
  counter.position.set(-0.28, 0, 0);

  // Short chain links hanging from counterweight
  for (let i = 0; i < 3; i++) {
    const link = new THREE.Mesh(new THREE.TorusGeometry(0.04, 0.012, 6, 10), iron);
    link.rotation.x = Math.PI / 2;
    link.position.set(-0.28, -0.18 - i * 0.07, 0);
    arm.add(link);
  }

  arm.add(shaft, wrap, grip, knob, counter);
  root.add(arm);

  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });

  parent.add(root);
  solids.push(makeAABB(x, 0.7, z, 0.7, 0.75, 0.6));

  return { root, arm };
}

/** Collect world-space AABB colliders from fort meshes. */
function collectMeshColliders(root: THREE.Object3D, solids: AABB[]): THREE.Object3D | null {
  root.updateMatrixWorld(true);
  let gate: THREE.Object3D | null = null;
  const tmp = new THREE.Box3();
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const name = (mesh.name || mesh.parent?.name || '').toLowerCase();
    if (name.includes('gate')) gate = mesh;

    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (Array.isArray(mesh.material)) {
      for (const m of mesh.material) {
        const std = m as THREE.MeshStandardMaterial;
        if (std.isMeshStandardMaterial) std.envMapIntensity = 0.55;
      }
    } else {
      const std = mesh.material as THREE.MeshStandardMaterial;
      if (std?.isMeshStandardMaterial) std.envMapIntensity = 0.55;
    }

    tmp.setFromObject(mesh);
    if (tmp.isEmpty()) return;
    const size = tmp.getSize(new THREE.Vector3());
    if (size.x < 0.05 && size.z < 0.05) return;
    if (size.y < 0.04 && size.x * size.z < 0.2) return;

    // Flatten near-horizontal slabs into thin floor colliders (avoid fat wall AABBs)
    const isFloorish = size.y < 0.85 && size.x > 1.2 && size.z > 1.2;
    if (isFloorish) {
      const midY = (tmp.min.y + tmp.max.y) * 0.5;
      solids.push({
        minX: tmp.min.x,
        maxX: tmp.max.x,
        minY: midY - 0.12,
        maxY: tmp.max.y + 0.02,
        minZ: tmp.min.z,
        maxZ: tmp.max.z,
      });
      return;
    }

    // Skip absurdly huge volumes that would fill the courtyard
    const volume = size.x * size.y * size.z;
    if (volume > 2200) return;

    solids.push(aabbFromBox3(tmp));
  });
  return gate;
}

function addEdgeScenery(parent: THREE.Object3D, mats: KeepMaterials, bounds: PlayBounds): void {
  const cliffMat = mats.wallDark.clone();
  cliffMat.color = new THREE.Color(0x2a2420);
  const voidMat = new THREE.MeshBasicMaterial({ color: 0x1a1614, fog: true });
  const cx = (bounds.minX + bounds.maxX) * 0.5;
  const cz = (bounds.minZ + bounds.maxZ) * 0.5;
  const spanX = bounds.maxX - bounds.minX + 12;
  const spanZ = bounds.maxZ - bounds.minZ + 12;
  const cliffs: Array<[number, number, number, number, number, number]> = [
    [cx, -4, bounds.minZ - 4, spanX, 10, 3],
    [cx, -4, bounds.maxZ + 4, spanX, 10, 3],
    [bounds.minX - 4, -4, cz, 3, 10, spanZ],
    [bounds.maxX + 4, -4, cz, 3, 10, spanZ],
  ];
  for (const [x, y, z, sx, sy, sz] of cliffs) {
    const geo = new THREE.BoxGeometry(sx, sy, sz);
    setBoxWorldUVs(geo, sx, sy, sz, 2.8);
    const mesh = new THREE.Mesh(geo, cliffMat);
    mesh.position.set(x, y, z);
    mesh.receiveShadow = true;
    parent.add(mesh);
  }
  const voidGeo = new THREE.PlaneGeometry(spanX + 40, spanZ + 40);
  const voidPlane = new THREE.Mesh(voidGeo, voidMat);
  voidPlane.rotation.x = -Math.PI / 2;
  voidPlane.position.set(cx, -8.5, cz);
  parent.add(voidPlane);
}

/**
 * Load Poly Haven Modular Fort 01 (CC0) as the keep, then place ladders / lever /
 * play bounds for the ICO-style demo loop.
 */
export async function buildWorld(scene: THREE.Scene): Promise<WorldData> {
  const solids: AABB[] = [];
  const climbables: Climbable[] = [];
  const root = new THREE.Group();
  root.name = 'QuietKeep';
  scene.add(root);

  const mats = createKeepMaterials();
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(FORT_URL);
  const fort = gltf.scene;
  fort.name = 'ModularFort01';

  // Center on origin, sit on y=0, scale for a playable courtyard slice
  fort.updateMatrixWorld(true);
  const rawBox = new THREE.Box3().setFromObject(fort);
  const rawSize = rawBox.getSize(new THREE.Vector3());
  const rawCenter = rawBox.getCenter(new THREE.Vector3());
  const targetSpan = 34;
  const scale = targetSpan / Math.max(rawSize.x, rawSize.z);
  fort.position.set(-rawCenter.x * scale, -rawBox.min.y * scale, -rawCenter.z * scale);
  fort.scale.setScalar(scale);
  root.add(fort);
  fort.updateMatrixWorld(true);
  weatherFortMaterials(fort);

  const fortBox = new THREE.Box3().setFromObject(fort);
  const fortSize = fortBox.getSize(new THREE.Vector3());
  const fortCenter = fortBox.getCenter(new THREE.Vector3());

  // Courtyard ground under the fort footprint
  const floorW = fortSize.x + 4;
  const floorD = fortSize.z + 20; // extend south under keep wing
  const floorGeo = new THREE.PlaneGeometry(floorW + 24, floorD + 8);
  setPlaneWorldUVs(floorGeo, floorW + 24, floorD + 8, 3.2);
  const floor = new THREE.Mesh(floorGeo, mats.ground);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(fortCenter.x, 0, fortCenter.z - 8);
  floor.receiveShadow = true;
  root.add(floor);
  solids.push(
    makeAABB(fortCenter.x, -0.5, fortCenter.z - 8, (floorW + 24) * 0.5, 0.5, (floorD + 8) * 0.5),
  );

  scatterMoss(root, mats, fortBox, fortCenter);

  const gateMesh =
    collectMeshColliders(fort, solids) ??
    addBox(root, mats.metal, fortCenter.x, 2.2, fortBox.max.z - 2, 3.4, 4.2, 0.45, solids, true);
  gateMesh.name = 'gate';

  const gatePos = new THREE.Vector3();
  gateMesh.getWorldPosition(gatePos);

  // Room 0 = sealed courtyard; north door faces the gate, south opens into the keep wing
  const courtyard = {
    minX: fortCenter.x - Math.min(10.5, fortSize.x * 0.26),
    maxX: fortCenter.x + Math.min(10.5, fortSize.x * 0.26),
    minZ: fortCenter.z - Math.min(7.5, fortSize.z * 0.14),
    maxZ: Math.max(gatePos.z + 0.8, fortCenter.z + fortSize.z * 0.1),
  };

  const rooms = buildKeepWing(root, mats, solids, courtyard);

  PLAY_BOUNDS = boundsFromRooms(rooms, fortBox.min.y - 3.5);
  addPerimeterShell(solids, PLAY_BOUNDS);
  addEdgeScenery(root, mats, PLAY_BOUNDS);

  // Spawn inside courtyard (room 0), clear of walls
  const spawn = new THREE.Vector3(
    (courtyard.minX + courtyard.maxX) * 0.5,
    0.12,
    courtyard.minZ + (courtyard.maxZ - courtyard.minZ) * 0.55,
  );
  const companionSpawn = spawn.clone().add(new THREE.Vector3(1.6, 0, 1.0));

  // Ladder onto walkway — prefer near stairs mesh if present
  let ladderX = fortCenter.x + fortSize.x * 0.18;
  let ladderZ = (courtyard.minZ + courtyard.maxZ) * 0.5;
  let ladderTop = Math.min(4.2, fortSize.y * 0.35);
  fort.traverse((o) => {
    const n = o.name.toLowerCase();
    if (!n.includes('stairs')) return;
    o.updateMatrixWorld(true);
    const b = new THREE.Box3().setFromObject(o);
    if (b.isEmpty()) return;
    const lx = (b.min.x + b.max.x) * 0.5;
    const lz = b.max.z + 0.35;
    if (lx > courtyard.minX + 1 && lx < courtyard.maxX - 1 && lz > courtyard.minZ + 1 && lz < courtyard.maxZ - 1) {
      ladderX = lx;
      ladderZ = lz;
      ladderTop = Math.max(2.8, b.max.y * 0.85);
    }
  });
  // Keep ladders inside courtyard bounds
  ladderX = Math.min(courtyard.maxX - 1.2, Math.max(courtyard.minX + 1.2, ladderX));
  ladderZ = Math.min(courtyard.maxZ - 1.5, Math.max(courtyard.minZ + 1.5, ladderZ));

  addLadder(root, mats, climbables, {
    centerX: ladderX,
    centerZ: ladderZ,
    bottomY: 0.08,
    topY: Math.min(ladderTop, 4.0),
    normal: { x: 0, y: 0, z: 1 },
    width: 0.95,
  });

  addLadder(root, mats, climbables, {
    centerX: Math.min(courtyard.maxX - 1.5, fortCenter.x - fortSize.x * 0.12),
    centerZ: Math.min(courtyard.maxZ - 2.0, courtyard.minZ + 3.5),
    bottomY: 0.08,
    topY: Math.min(3.6, ladderTop),
    normal: { x: 0, y: 0, z: 1 },
    width: 0.9,
  });

  const leverX = Math.min(courtyard.maxX - 1.8, gatePos.x + 2.2);
  const leverZ = Math.min(courtyard.maxZ - 1.5, Math.max(courtyard.minZ + 2, gatePos.z - 1.8));

  // Stone steps toward gate inside courtyard
  for (let i = 0; i < 4; i++) {
    const h = 0.28 * (i + 1);
    const sz = Math.min(gatePos.z - 2.8 + i * 0.45, courtyard.maxZ - 0.8);
    if (sz < courtyard.minZ + 1) continue;
    addBox(
      root,
      mats.paving,
      gatePos.x,
      h * 0.5,
      sz,
      2.8,
      h,
      0.48,
      solids,
      true,
      1.3,
    );
  }

  for (let i = 0; i < 3; i++) {
    const mossStep = new THREE.Mesh(
      new THREE.BoxGeometry(1.1 + i * 0.15, 0.06, 0.22),
      mats.moss,
    );
    mossStep.position.set(
      gatePos.x + (i % 2 === 0 ? -0.7 : 0.7),
      0.28 * (i + 1) + 0.04,
      Math.min(gatePos.z - 2.8 + i * 0.45, courtyard.maxZ - 1),
    );
    mossStep.receiveShadow = true;
    root.add(mossStep);
  }

  const { root: leverPivot, arm: leverArm } = addGateLever(
    root,
    mats,
    leverX,
    leverZ,
    solids,
  );

  const leverMoss = new THREE.Mesh(new THREE.CircleGeometry(0.85, 16), mats.moss);
  leverMoss.rotation.x = -Math.PI / 2;
  leverMoss.position.set(leverX, 0.03, leverZ);
  leverMoss.receiveShadow = true;
  root.add(leverMoss);

  const interactables: Interactable[] = [
    {
      id: 'lever',
      aabb: makeAABB(leverX, 1.25, leverZ, 1.8, 1.5, 1.8),
      prompt: 'Press E — pull the winch lever',
      used: false,
    },
  ];

  return {
    solids,
    climbables,
    interactables,
    spawn,
    companionSpawn,
    gateMesh,
    leverPivot,
    leverArm,
    playBounds: { ...PLAY_BOUNDS },
    rooms,
  };
}

export function createDust(
  scene: THREE.Scene,
  biasToward?: THREE.Vector3,
): THREE.Points {
  const count = 700;
  const positions = new Float32Array(count * 3);
  const sun = biasToward?.clone().normalize() ?? new THREE.Vector3(-0.55, 0.72, 0.35);
  for (let i = 0; i < count; i++) {
    // Bias into a sun-shaft volume over the courtyard
    const along = Math.random() * 22;
    const side = (Math.random() - 0.5) * 6;
    const up = Math.random() * 10 + 0.4;
    const lateral = new THREE.Vector3(-sun.z, 0, sun.x).normalize();
    const p = sun.clone().multiplyScalar(along).addScaledVector(lateral, side);
    p.y = up + sun.y * along * 0.15;
    // Mix with a little uniform courtyard fill
    if (Math.random() < 0.35) {
      positions[i * 3] = (Math.random() - 0.5) * 36;
      positions[i * 3 + 1] = Math.random() * 12 + 0.4;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 36;
    } else {
      positions[i * 3] = p.x;
      positions[i * 3 + 1] = Math.max(0.3, p.y);
      positions[i * 3 + 2] = p.z;
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color: 0xffe6c8,
    size: 0.06,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
    sizeAttenuation: true,
  });
  const points = new THREE.Points(geo, mat);
  scene.add(points);
  return points;
}
